import type { ScopeEnvironment, TenantScope } from '@onefootprint/request-types/dashboard';

type Access = 'none' | 'sandbox_only' | 'sandbox_and_production';

/** One entry per permission in the role editor. `decrypt` covers every decrypt grant. */
type AccessByPermission = Partial<Record<TenantScope['kind'], Access>>;

type WithoutEnvironments<Scope> = Scope extends unknown ? Omit<Scope, 'environments'> : never;

const decryptKinds: TenantScope['kind'][] = [
  'decrypt',
  'decrypt_all',
  'decrypt_all_except_pci_data',
  'decrypt_custom',
  'decrypt_document',
  'decrypt_document_and_selfie',
];

// Mirrors the backend's per-environment `TenantScope` variants.
const sandboxablePermissions: TenantScope['kind'][] = [
  'decrypt',
  'api_keys',
  'manual_review',
  'write_entities',
  'label_and_tag',
];

const permissionOf = (scope: Pick<TenantScope, 'kind'>) => (decryptKinds.includes(scope.kind) ? 'decrypt' : scope.kind);

const isSandboxOnly = (scope: TenantScope) =>
  'environments' in scope && scope.environments !== undefined && !scope.environments.includes('live');

const canLimitToSandbox = (permission: TenantScope['kind']) => sandboxablePermissions.includes(permission);

const getAccess = (scopes: TenantScope[]): AccessByPermission => {
  const access: AccessByPermission = {};
  scopes.forEach(scope => {
    const permission = permissionOf(scope);
    // Mixed grants read as sandbox only, so saving the form never widens them.
    access[permission] = isSandboxOnly(scope) ? 'sandbox_only' : (access[permission] ?? 'sandbox_and_production');
  });
  return access;
};

const withEnvironments = (scope: WithoutEnvironments<TenantScope>, access?: Access) => {
  const environments: ScopeEnvironment[] = access === 'sandbox_only' ? ['sandbox'] : ['sandbox', 'live'];
  // TS can't tell that only the per-environment kinds need `environments`.
  return (canLimitToSandbox(permissionOf(scope)) ? { ...scope, environments } : scope) as TenantScope;
};

const withAccess = (scopes: TenantScope[], access: AccessByPermission): TenantScope[] =>
  scopes.map(scope => withEnvironments(scope, access[permissionOf(scope)]));

export type { Access, AccessByPermission, WithoutEnvironments };
export { canLimitToSandbox, getAccess, isSandboxOnly, withAccess, withEnvironments };
